import { useMemo } from "react";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import { ActiveFilter, SortOption } from "components/ControlBar/types";
import FilterButton from "components/Button/FilterButton";
import { FiBriefcase, FiHome, FiMapPin, FiUsers } from "react-icons/fi";
import { GROUP_TYPES, GroupType, GroupsFilters } from "../types";

const SORT_OPTIONS: SortOption[] = [
  { label: "Name (A-Z)", value: "name_asc" },
  { label: "Name (Z-A)", value: "name_desc" },
  { label: "Most members", value: "member_count_desc" },
  { label: "Recently synced", value: "scim_last_synced_at_desc" },
];

const DEFAULT_SORT = "name_asc";

const TYPE_LABEL: Record<GroupType, string> = {
  team: "Teams",
  department: "Departments",
  location: "Locations",
  role: "Roles",
};

const TYPE_ICON: Record<GroupType, typeof FiUsers> = {
  team: FiUsers,
  department: FiBriefcase,
  location: FiMapPin,
  role: FiHome,
};

interface GroupFiltersProps {
  filters: GroupsFilters;
  currentSort?: string;
  searchValue?: string;
  onFiltersChange: (filters: GroupsFilters) => void;
  onSortChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onReset: () => void;
}

export default function GroupFilters({
  filters,
  currentSort = DEFAULT_SORT,
  searchValue,
  onFiltersChange,
  onSortChange,
  onSearchChange,
  onReset,
}: GroupFiltersProps) {
  const toggleType = (type: GroupType) => {
    onFiltersChange({
      ...filters,
      groupType: filters.groupType === type ? undefined : type,
    });
  };

  const activeFilters = useMemo<ActiveFilter[]>(() => {
    const out: ActiveFilter[] = [];
    if (filters.groupType) {
      out.push({
        key: "groupType",
        label: TYPE_LABEL[filters.groupType],
        onRemove: () => onFiltersChange({ ...filters, groupType: undefined }),
      });
    }
    return out;
  }, [filters, onFiltersChange]);

  const quickFilters = (
    <>
      {GROUP_TYPES.map((type) => (
        <FilterButton
          key={type}
          onClick={() => toggleType(type)}
          isActive={filters.groupType === type}
          icon={TYPE_ICON[type]}
          size="sm"
        >
          {TYPE_LABEL[type]}
        </FilterButton>
      ))}
    </>
  );

  return (
    <FilterSortSearchBar
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search groups..."
      currentSort={currentSort}
      defaultSort={DEFAULT_SORT}
      onSortChange={onSortChange}
      sortOptions={SORT_OPTIONS}
      quickFilters={quickFilters}
      allFilters={quickFilters}
      activeFilters={activeFilters}
      onReset={onReset}
      hasActiveFilters={activeFilters.length > 0 || !!searchValue}
    />
  );
}
